// client/src/components/world/InteriorVisualizer.jsx
// Итерация 8.1 + 8.3a: рендер интерьера структуры (только текущая палуба + радиус).
//
// Контракт:
//   <InteriorVisualizer
//     installedModules={structureInfo?.installed_modules || []}
//     currentDeckId={selectedDeckId || characterMeta?.deck_id || null}
//     characterPosition={characterState?.position || null}
//     visibilityRadius={100}
//   />
//
// Логика:
//   1. prepareModules — валидная позиция → палуба → радиус (если > 200).
//   2. findCurrentModule — nearest neighbor (подсветка).
//   3. < 50 модулей → <group> с <ModulePrimitive>.
//   4. >= 50 модулей → <InstancedModules> (LOD).
//   5. Пусто → null (WorldScene нарисует <LocationVisualizer>).
//
// ⚠️ Не знает про Navigator, WorldScene, Redux. Только props.

import React, { useMemo } from 'react';
import ModulePrimitive from './ModulePrimitive';
import InstancedModules from './InstancedModules';
import {
  prepareModules,
  findCurrentModule,
} from '../../utils/structureGeometry';

// Порог переключения на instancedMesh
const INSTANCED_THRESHOLD = 50;

// ============================================================================
// КОМПОНЕНТ
// ============================================================================

export default function InteriorVisualizer({
  installedModules,
  currentDeckId,
  characterPosition,
  visibilityRadius = 100,
}) {
  // 1. Подготовка модулей (валидная позиция + палуба + радиус)
  const modules = useMemo(
    () =>
      prepareModules(
        installedModules,
        currentDeckId,
        characterPosition,
        visibilityRadius
      ),
    [installedModules, currentDeckId, characterPosition, visibilityRadius]
  );

  // 2. Ближайший модуль (где стоит персонаж)
  const currentModule = useMemo(
    () => findCurrentModule(modules, characterPosition),
    [modules, characterPosition]
  );

  const currentModuleId = currentModule?.instance_id || currentModule?.id || null;

  // 3. Пусто → null (WorldScene нарисует <LocationVisualizer>)
  if (!modules || modules.length === 0) {
    return null;
  }

  // 4. < 50 модулей → group с <ModulePrimitive>
  if (modules.length < INSTANCED_THRESHOLD) {
    return (
      <group name="interior-modules">
        {modules.map((module, idx) => {
          const key = module.instance_id || module.id || `module-${idx}`;
          const isCurrent =
            currentModuleId !== null &&
            (module.instance_id === currentModuleId ||
              module.id === currentModuleId);

          return (
            <ModulePrimitive
              key={key}
              module={module}
              highlight={isCurrent}
            />
          );
        })}
      </group>
    );
  }

  // 5. >= 50 модулей → instancedMesh (8.2)
  return <InstancedModules modules={modules} currentModuleId={currentModuleId} />;
}

// ============================================================================
// ЭКСПОРТЫ ДЛЯ ТЕСТИРОВАНИЯ
// ============================================================================

export { INSTANCED_THRESHOLD };
